import * as React from 'react';
import type { PressEvent, PressHookProps } from 'react-aria';
import {
    type OverlayTriggerProps as StatelyOverlayTriggerProps,
    useOverlayTriggerState as useStatelyOverlayTriggerState,
} from 'react-stately';

import { useEventCallback, useMergedRefs } from '@gitbook/util-react-hooks';
import { assert, assertNever } from '@gitbook/util-ts';

import type { OverlayRootProps } from './OverlayRoot';

export type OverlayTriggerAction = 'hover' | 'press';

export type OverlayTriggerStateProps = StatelyOverlayTriggerProps &
    Partial<Pick<OverlayTriggerState, 'action' | 'delay'>> & {
        /**
         * Ref to the trigger element.
         */
        ref?: React.MutableRefObject<HTMLElement | null>;
    };

// Mimic the API of react-stately's OverlayTriggerState
// but their API has a problem with this because of function definitions.
export type OverlayTriggerState = {
    /** The ID of the overlay target element. */
    targetId: string;
    /** Ref to the trigger element. */
    ref: React.MutableRefObject<HTMLElement | null>;

    /**
     * Props to pass to the root element of the overlay.
     */
    rootProps: Omit<OverlayRootProps, 'children'>;

    /**
     * Action that triggers.
     * @default "press"
     */
    action: OverlayTriggerAction;

    /**
     * Delay for action "hover".
     * @default 300
     */
    delay: number;

    /**
     * Timeout ref used for hover delay purposes.
     */
    timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;

    // Props from react-stately's OverlayTriggerState

    /** Whether the overlay is currently open. */
    readonly isOpen: boolean;
    /** Sets whether the overlay is open. */
    setOpen: (isOpen: boolean) => void;
    /** Opens the overlay. */
    open: () => void;
    /** Closes the overlay. */
    close: () => void;
    /** Toggles the overlay's visibility. */
    toggle: () => void;
};

/**
 * Hook to get the state for an overlay trigger.
 */
export function useOverlayTriggerState(props?: OverlayTriggerStateProps): OverlayTriggerState {
    const targetId = React.useId();
    const defaultTriggerRef = React.useRef<HTMLDivElement>(null);
    const triggerRef = props?.ref ?? defaultTriggerRef;
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const handleOpenChange = useEventCallback((isOpen: boolean) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        props?.onOpenChange?.(isOpen);
    });
    const { close, isOpen, open, setOpen, toggle } = useStatelyOverlayTriggerState({
        ...props,
        onOpenChange: handleOpenChange,
    });
    const action = props?.action ?? 'press';
    const delay = props?.delay ?? 300;

    return React.useMemo(
        () => ({
            close,
            isOpen,
            open,
            setOpen,
            toggle,
            targetId,
            ref: triggerRef,
            rootProps: {
                isOpen: isOpen,
                onOpenChange: setOpen,
                id: targetId,
                triggerRef,
            },
            action,
            delay,
            timeoutRef,
        }),
        [close, isOpen, open, setOpen, toggle, targetId, triggerRef, action, delay]
    );
}

export const OverlayTriggerContext = React.createContext<OverlayTriggerState | null>(null);

export type OverlayTriggerProviderProps = {
    trigger: OverlayTriggerState;
    children: React.ReactNode;
};

export function OverlayTriggerProvider(props: OverlayTriggerProviderProps) {
    return (
        <OverlayTriggerContext.Provider value={props.trigger}>
            {props.children}
        </OverlayTriggerContext.Provider>
    );
}

export type OverlayTriggerProps = {
    /**
     * The trigger state for the overlay. If not provided, the component must be used within a OverlayRoot.
     */
    trigger?: OverlayTriggerState;
    /**
     * The children to render. Must be a single React element.
     */
    children: React.ReactNode;
    /**
     * The value of the `aria-haspopup` attribute for the trigger element.
     */
    ariaHasPopup?: React.AriaAttributes['aria-haspopup'];
};

/**
 * Props that an overlay trigger child should accept.
 */
type OverlayTriggerChildProps = React.AriaAttributes & {
    onPress?: PressHookProps['onPress'];
    onMouseOver?: React.MouseEventHandler;
    onMouseOut?: React.MouseEventHandler;
    id?: string;
    ref?: React.RefCallback<HTMLElement | null>;
};

/**
 * A component that wraps a trigger element and opens an overlay when the trigger is activated.
 */
export function OverlayTrigger(props: OverlayTriggerProps) {
    const child = React.Children.only(props.children);
    assert(
        React.isValidElement<OverlayTriggerChildProps>(child),
        'OverlayTrigger must have a single React element as its child.'
    );

    const context = React.useContext(OverlayTriggerContext);

    const trigger = props.trigger ?? context;
    if (!trigger) {
        throw new Error(
            'OverlayTrigger must be used within a OverlayTriggerProvider or with a `trigger` prop.'
        );
    }

    const mergeRef = useMergedRefs<HTMLElement>(trigger.ref, (child as any).ref);

    return React.cloneElement(child, {
        ref: mergeRef,
        'aria-haspopup': props.ariaHasPopup ?? 'true',
        'aria-expanded': trigger.isOpen,
        'aria-controls': trigger.targetId,
        ...getOverlayTriggerProps(trigger, child.props),
    });
}

/**
 * Get props for the overlay from a trigger.
 */
export function getOverlayProps(trigger: OverlayTriggerState, props: OverlayTriggerChildProps) {
    switch (trigger.action) {
        case 'hover':
            return getHoverActionProps(trigger, props);
        case 'press':
            return {};
        default:
            assertNever(trigger.action);
    }
}

/**
 * Get props for an overlay trigger.
 */
function getOverlayTriggerProps(trigger: OverlayTriggerState, props: OverlayTriggerChildProps) {
    switch (trigger.action) {
        case 'hover':
            return getHoverActionProps(trigger, props);
        case 'press':
            return getPressActionProps(trigger, props);
        default:
            assertNever(trigger.action);
    }
}

/**
 * Get "press" action props.
 */
function getPressActionProps(trigger: OverlayTriggerState, props: OverlayTriggerChildProps) {
    assert(trigger.action === 'press');
    return {
        onPress: (event: PressEvent) => {
            props.onPress?.(event);
            trigger.open();
        },
    };
}

/**
 * Get "hover" action props.
 */
function getHoverActionProps(trigger: OverlayTriggerState, props: OverlayTriggerChildProps) {
    assert(trigger.action === 'hover');

    const onMouseOver: React.MouseEventHandler = (event) => {
        props?.onMouseOver?.(event);
        if (trigger.timeoutRef.current) {
            clearTimeout(trigger.timeoutRef.current);
        }
        trigger.timeoutRef.current = setTimeout(() => {
            trigger.open();
        }, trigger.delay);
    };

    const onMouseOut: React.MouseEventHandler = (event) => {
        props?.onMouseOut?.(event);
        if (trigger.timeoutRef.current) {
            clearTimeout(trigger.timeoutRef.current);
        }
        // We delay the close to let the mouse have time to hover on the overlay.
        trigger.timeoutRef.current = setTimeout(() => {
            trigger.close();
        }, 150);
    };

    return { onMouseOver, onMouseOut };
}

/**
 * Hook to get the close function for the current overlay trigger.
 */
export function useOverlayClose() {
    const context = React.useContext(OverlayTriggerContext);
    assert(context, 'useOverlayClose must be used within a OverlayTriggerProvider.');
    return context.close;
}

/**
 * Hook to get the ref for the current overlay trigger.
 */
export function useOverlayTriggerRef<T extends HTMLElement = HTMLElement>(): React.RefObject<T> {
    const context = React.useContext(OverlayTriggerContext);
    assert(context, 'useOverlayTriggerRef must be used within a OverlayTriggerProvider.');

    // @ts-expect-error
    return context.ref;
}
